import { useEffect, useMemo, useState } from "react";
import { todayIso } from "../docs/helpers";
import { createPortal } from "react-dom";
import { api } from "../api";
import type { OpDay } from "../types";

const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

/** «01», «02» … — в календаре числа читают столбиком, одной ширины. */
const pad = (n: number) => String(n).padStart(2, "0");

/** «2026-09-15» → 2026, 8, 15 без часовых поясов: даты приходят строками. */
function parts(iso: string): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)!;
  return [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
}
const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/**
 * Календарь оперденей — куда записать пациента из листа ожидания.
 *
 * Выбирают не дату, а существующий опердень: премиального пациента оперируют в
 * общий день ФЭК, и произвольное число здесь означало бы день, которого никто
 * не открывал. Поэтому кликабельны только заведённые дни, а месяц открывается
 * тот, где ближайший из них.
 *
 * На одну дату бывает два дня (ОМС и платный) — тогда день выбирается вторым
 * шагом, из списка под сеткой: свести их в одну клетку значило бы записать
 * пациента наугад в один из двух.
 */
/** Фамилия из «Иванов И.И.» — так же считает загрузку хирургов сервер. */
const surname = (fio: string) => String(fio ?? "").trim().split(/\s+/)[0] ?? "";

export default function OpDayPicker({
  anchor,
  current,
  surgeon,
  onPick,
  onClear,
  onClose,
  days: given,
  startMonth,
  want,
  className,
}: {
  /** Кнопка «назначить», под которой открывается календарь. */
  anchor: DOMRect;
  /** Уже назначенный день, чтобы показать его выбранным. */
  current: number | null;
  /** Хирург записи — в подсказке дня показывается его загрузка. */
  surgeon?: string;
  onPick: (day: OpDay) => void;
  /** «Снять дату»; без него ссылки нет. */
  onClear?: () => void;
  onClose: () => void;
  /** Дни на выбор, если не все дни записи (напоминание — дни одного списка). */
  days?: OpDay[];
  /** С какого месяца открыть («2027-03-01»): напоминание — с нужного пациенту. */
  startMonth?: string | null;
  /** Месяцы, в которые пациент хочет оперироваться, — клетки обведены. */
  want?: { from: string; to: string } | null;
  /** Класс поверх: в окне карточки календарь должен лечь выше её подложки. */
  className?: string;
}) {
  const [days, setDays] = useState<OpDay[] | null>(null);
  const [month, setMonth] = useState<{ y: number; m: number } | null>(null);
  const [pickedDate, setPickedDate] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const open = (list: OpDay[]) => {
      setDays(list);
      const first = list.find((d) => d.day_id === current) ?? list[0];
      const [y, m] = startMonth ? parts(startMonth) : first ? parts(first.op_date) : parts(todayIso());
      setMonth({ y, m });
    };
    if (given) {
      open(given);
      return;
    }
    api
      .planDays()
      .then((r) => alive && open(r.days))
      .catch(() => alive && setDays([]));
    return () => {
      alive = false;
    };
  }, [current, given, startMonth]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  /** Дни месяца, у которых есть опердень, — по дате. */
  const byDate = useMemo(() => {
    const map = new Map<string, OpDay[]>();
    for (const d of days ?? []) {
      const list = map.get(d.op_date) ?? [];
      list.push(d);
      map.set(d.op_date, list);
    }
    return map;
  }, [days]);

  // Через портал и fixed: .op-block прокручивается, и календарь, оставшись в
  // строке, обрезался бы её краем — как и любой popup в этом приложении.
  const WIDTH = 300;
  const style: React.CSSProperties = {
    position: "fixed",
    left: Math.min(anchor.left, Math.max(8, window.innerWidth - WIDTH - 8)),
    width: WIDTH,
    ...(anchor.bottom + 320 > window.innerHeight && anchor.top > 340
      ? { bottom: window.innerHeight - anchor.top + 4 }
      : { top: anchor.bottom + 4 }),
  };

  const rootClass = ["opw-cal", className].filter(Boolean).join(" ");
  if (!month) return createPortal(<div className={`${rootClass} opw-cal-loading`} style={style} />, document.body);

  const first = new Date(month.y, month.m, 1);
  // Понедельник — первый столбец: так печатают расписание в клинике.
  const lead = (first.getDay() + 6) % 7;
  const total = new Date(month.y, month.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];
  while (cells.length % 7) cells.push(null);

  const shift = (by: number) => {
    const d = new Date(month.y, month.m + by, 1);
    setMonth({ y: d.getFullYear(), m: d.getMonth() });
    setPickedDate(null);
  };

  const sameDate = pickedDate ? (byDate.get(pickedDate) ?? []) : [];

  return createPortal(
    <div className={rootClass} style={style}>
      <div className="opw-cal-head">
        <i className="opw-cal-prev" title="Раньше" onClick={() => shift(-1)} />
        <span className="opw-cal-month">
          <b>{MONTHS[month.m]}</b>
          {month.y}
        </span>
        <i className="opw-cal-next" title="Позже" onClick={() => shift(1)} />
      </div>

      {/* Дни недели — на своей плашке, как шапка таблицы: сетка чисел под ней
          читается столбиками, а не сплошным полем. */}
      <div className="opw-cal-wds">
        {WEEKDAYS.map((w) => (
          <div className="opw-cal-wd" key={w}>
            {w}
          </div>
        ))}
      </div>

      <div className="opw-cal-grid">
        {cells.map((d, i) => {
          if (d === null) return <div className="opw-cal-cell opw-cal-empty" key={`e${i}`} />;
          const date = iso(month.y, month.m, d);
          const open = byDate.get(date) ?? [];
          const has = open.length > 0;
          const isCurrent = open.some((o) => o.day_id === current);
          // Сколько всего записано на дату (по всем дням даты) и сколько из
          // них — у хирурга этой записи: видно, куда ещё есть смысл ставить.
          const total = open.reduce((n, o) => n + (o.taken ?? 0), 0);
          const mine = surname(surgeon ?? "");
          const mineN = mine
            ? open.reduce((n, o) => n + ((o.surgeons ?? []).find((x) => x.surgeon === mine)?.n ?? 0), 0)
            : 0;
          const hint = has
            ? [
                ...open.map((o) => `${o.label}${o.planned > 0 ? ` · ${o.taken} из ${o.planned}` : ` · ${o.taken}`}`),
                `Всего записано: ${total}`,
                ...(mine ? [`${mine}: ${mineN}`] : []),
              ].join("\n")
            : "Опердень не заведён";
          return (
            <div
              key={date}
              className={[
                "opw-cal-cell",
                has ? "opw-cal-open" : "opw-cal-shut",
                pickedDate === date ? "opw-cal-picked" : null,
                isCurrent ? "opw-cal-current" : null,
                want && date >= want.from && date <= want.to ? "opw-cal-want" : null,
              ]
                .filter(Boolean)
                .join(" ")}
              title={hint}
              onClick={() => {
                if (!has) return;
                // Одна дата — один день: выбирать не из чего, ставим сразу.
                if (open.length === 1) onPick(open[0]);
                else setPickedDate(date);
              }}
            >
              {pad(d)}
              {has && <i title={hint}>{total}</i>}
            </div>
          );
        })}
      </div>

      {sameDate.length > 1 && (
        <div className="opw-cal-pick">
          <div className="opw-cal-pick-hint">На эту дату два дня — выберите:</div>
          {sameDate.map((d) => (
            <u className="hist-link" key={d.day_id} onClick={() => onPick(d)}>
              {d.label}
              {d.planned > 0 ? ` · ${d.taken} из ${d.planned}` : ""}
            </u>
          ))}
        </div>
      )}

      <div className="opw-cal-foot">
        {days && days.length === 0 && (
          <span className="opw-cal-none">Впереди нет заведённых опердней.</span>
        )}
        {current && onClear && (
          <u className="hist-link" onClick={onClear}>
            снять дату
          </u>
        )}
      </div>
    </div>,
    document.body
  );
}
